import { useCallback } from 'react';
import { differenceInMinutes } from 'date-fns';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { RoleType } from '@sdk/authentication/types';
import { ShouldShowElement } from '@sdk/authentication/types/apiTypes';

import { useWebClientFlags } from '@web/common/hooks/useWebClientFlags';

type Options = {
  delayInMinutes?: number;
  roles?: RoleType[];
  validate?: () => boolean;
};

export const useShouldShow = (
  what: ShouldShowElement,
  currentUser: AuthUserType,
  { delayInMinutes, roles, validate }: Options = {}
) => {
  const { updateClientFlags } = useWebClientFlags();
  const currentFlags = currentUser.settings.clientFlags.dontShowAgain || {};

  const dontShowAgain = (value = true) =>
    updateClientFlags({ dontShowAgain: { ...currentFlags, [what]: value } });

  const shouldShow = useCallback(() => {
    const timeHasPassed =
      !delayInMinutes ||
      !!(differenceInMinutes(new Date(), new Date(currentUser.org.createdAt)) > delayInMinutes);

    if (roles && !roles.includes(currentUser.role)) {
      return false;
    }

    if (!timeHasPassed) {
      return false;
    }

    if (validate && !validate()) {
      return false;
    }

    return !currentUser.settings.clientFlags.dontShowAgain?.[what];
  }, [
    what,
    currentUser.org.createdAt,
    currentUser.role,
    currentUser.settings.clientFlags.dontShowAgain,
    delayInMinutes,
    roles,
    validate,
  ]);

  return { dontShowAgain, shouldShow };
};
